import BootstrapModal from "bootstrap/js/dist/modal";

// Common data loading tools and format conversions

const asyncLoadingTemplate = `
    <div class="async-element-loading">
        <div class="pots-row-loader">
            <img src="/content/img/loader/pots-row.svg" alt="Loading..." style="opacity: 0.64;" />
        </div>
        <p class="font:label-s no-margin">&nbsp;&nbsp;&nbsp;Loading...</p>
    </div>
`;
const asyncErrorTemplate = `
    <div class="async-element-error">
        <div class="alert alert-danger alert-with-icon" role="alert">
            <svg role="img" width="24" height="24" aria-label="Error:" viewBox=" 0 0 24 24">
                <path fill="#042E53" fill-rule="evenodd"
                    d="M13 3.1c-.5-.8-1.6-.8-2 0L1.7 19.7c-.2.4-.3.9 0 1.3.2.3.5.5.9.5h18.8c.9 0 1.4-.9.9-1.7L12.9 3Zm-3.3-.7c1-1.9 3.5-1.9 4.5 0L23.6 19c1.1 1.8-.2 4-2.2 4H2.6c-2 0-3.2-2.3-2.2-4L9.7 2.4Z" clip-rule="evenodd" />
                <path fill="#042E53" fill-rule="evenodd" d="M12 6.5c.4 0 .7.3.7.7V13a.8.8 0 0 1-1.5 0V7.2c0-.4.3-.7.8-.7Z" clip-rule="evenodd" />
                <path fill="#D22600" stroke="#042E53" d="M12.7 17.8a.8.8 0 1 1-1.5 0 .8.8 0 0 1 1.5 0Z" />
            </svg>
            <span class="alert-message"> Sorry, there was a problem connecting to the server. Please try again later.</span>
        </div>
    </div>
`;

/**
 * Calls fetch, but throws an error if unsuccessful
 * @returns {Response}
 */
export async function FetchWithErrors() {
    const response = await fetch.apply(null, arguments);
    if (!response.ok) {
        const err = new Error("HTTP request failed: " + response.status);
        err.response = response;
        err.status = response.status;
        // If we got a JSON message prop back, use that as the error message
        try {
            const json = await response.json();
            if (json) {
                if (json.message) err.message = json.message;
            }
        } catch {
            // OK to ignore, we're going to throw anyway
        }
        throw err;
    }
    return response;
}

/**
 * Wrapper to de-jsonify and because MVC wants JSON requests to be posts
 */
export async function PostJSON(route, data, headers) {
    const response = await FetchWithErrors(route ?? window.location.href, {
        method: "POST",
        headers: headers ?? {
            "Accept": "application/json",
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(data),
    });
    // TODO: this is a temporary extra message to debug the volunteer js issue, TR-1534
    // Go back to using plain response.json(), maybe keep throw new Error("Unable to parse server response")
    const text = await response.text();
    try {
        return JSON.parse(text);
    } catch {
        // extract the html title
        const titleMatch = text.match(/<title>(.*?)<\/title>/);
        // display the title
        throw new Error("Unable to parse server response (" + response.status + (response.redirected ? ", redirected, " + response.url : "") + ")" + ((titleMatch && titleMatch.length >= 1) ? ("\n" + titleMatch[1]) : ""));
    }
}

/**
 * Converts a Data URL to a File object
 * @param {string} dataurl DataURL, with content-type included
 * @param {string} fileName File's filename
 */
async function DataUrlToFile(dataurl, fileName) {
    const response = await fetch(dataurl);
    const buffer = await response.arrayBuffer();
    const file = new File([buffer], fileName, { type: response.headers.get("content-type") });
    return file;
}

/**
 * @callback ProgressCallback
 * @param {Number} complete The fraction of work completed, 0-1
 */

/**
 * Post a file to an endpoint as multipart form data
 * @param {String} route Endpoint to post to
 * @param {String} dataurl File contents in dataurl format
 * @param {String} fileName Filename to send as
 * @param {ProgressCallback} progressCallback Called when there is a progress update, with the percentage complete
 * @returns {String} Full response 
 */
export async function PostMultipartFile(route, dataurl, fileName, progressCallback) {
    const file = await DataUrlToFile(dataurl, fileName);
    const formData = new FormData();
    formData.append("file", file);
    // Need to use XMLHttpRequest because fetch doesn't support upload progress
    const request = new XMLHttpRequest();
    return new Promise((resolve, reject) => {
        request.upload.addEventListener("progress", e => {
            progressCallback(e.loaded / e.total);
        });
        request.upload.addEventListener("error", reject);
        request.upload.addEventListener("abort", reject);
        request.upload.addEventListener("timeout", reject);
        request.addEventListener("load", () => {
            if (request.status >= 200 && request.status < 300) {
                resolve(request.response);
            } else {
                let message = "Unable to upload the file";
                try {
                    const json = JSON.parse(request.response);
                    if (json && json.message) message = json.message;
                } catch {
                    // Didn't have a JSON response
                }
                reject(new Error(message));
            }

        });
        request.open("POST", route);
        request.setRequestHeader("X-Requested-With", "XMLHttpRequest");
        request.send(formData);
    });
}

/**
 * Loads a remote-generated modal, with loading and error display
 * @param {string} elementId ID of the modal element to replace contents of
 * @param {string} route Route to fetch the modal contents from
 * @param {boolean} isModal If true, will tell bootstrap to show and update the modal
 */
export async function LoadElementAsync(elementId, route, isModal = false) {
    // set the "loading" display
    const element = document.getElementById(elementId).querySelector("[data-replacement-target]");
    element.innerHTML = asyncLoadingTemplate;

    let modal;
    if (isModal) {
        // tell bootstrap to display the modal
        modal = new BootstrapModal("#" + elementId);
        modal.show();
    }

    try {
        // fetch and replace the modal html
        const response = await FetchWithErrors(route);
        const html = await response.text();
        element.innerHTML = html;
    } catch (err) {
        // set the "error" display
        element.innerHTML = asyncErrorTemplate;
        console.error(err);
    }
    if (isModal) {
        modal.handleUpdate();
    }
}

/**
 * Gets JSON, but also populates a loader element and, if necessary, an error response
 * @param {HTMLElement} statusElement An element to hold the loader/error. Cleared on success.
 * @param {string} route JSON route to fetch
 * @param {number | boolean} loadingHeight Explicitly set the loading div's height to the same height of the previous content. Set to true to use statusElement's height, or a number for explicit px.
 * @returns The data from the JSON route
 */
export async function PostJSONWithLoadError(statusElement, route, data, loadingHeight = false) {
    if (loadingHeight) {
        if (loadingHeight === true) loadingHeight = statusElement.offsetHeight;
        if (loadingHeight > 200) {
            statusElement.style.height = loadingHeight + "px";
        }
    }
    statusElement.innerHTML = asyncLoadingTemplate;
    try {
        const result = await PostJSON(route, data);
        statusElement.innerHTML = "";
        return result;
    } catch (err) {
        statusElement.innerHTML = asyncErrorTemplate;
        throw err;
    } finally {
        if (loadingHeight && loadingHeight > 200) {
            statusElement.style.height = null;
        }
    }
}

/**
 * Same as PostJSONWithLoadError, but for multiple loaders.
 * Weird case, shouldn't be used in general.
 * @param {Array<HTMLElement>} statusElements An array of elements to hold the loader/error. Cleared on success.
 * @param {string} route JSON route to fetch
 * @returns The data from the JSON route
 */
export async function PostJSONMultiLoader(statusElements, route) {
    statusElements.forEach(e => e.innerHTML = asyncLoadingTemplate);
    try {
        const result = await PostJSON(route);
        statusElements.forEach(e => e.innerHTML = "");
        return result;
    } catch (err) {
        statusElements.forEach(e => e.innerHTML = asyncErrorTemplate);
        throw err;
    }
}
